/* eslint-disable no-param-reassign */

'use strict';

define('vb/private/vx/extensionRegistry',[
  'vb/private/vx/extension',
  'vb/private/configLoader',
  'vb/private/vx/appUiInfos',
  'compare-versions',
  'vb/private/constants',
  'vb/private/utils',
  'vbc/private/log',
], (Extension, ConfigLoader, AppUiInfos, compareVersions, Constants, Utils, Log) => {
  const logger = Log.getLogger('/vb/private/vx/extensionRegistry');

  /**
   * @typedef {import('vb/private/stateManagement/container').default} Container
   * @typedef {import('vb/private/translations/bundleV2Definition').default} BundleV2Definition
   */

  /**
   * For v2, the base path is prefixed with 'ui/'
   * @param  {String} path
   * @param  {Container} container
   * @return {String}
   */
  const getBasePathForUi = (path, container) => `${Constants.DefaultPaths.UI}${container.getBasePath(path)}`;

  /**
   * Retrieve the base path for an extension layout in v2
   * Convert dynamicLayouts/{path} or dynamicLayouts/self/{path} when the container
   * is in an App UI to dynamicLayouts/{extId}/{path}
   * extId is the extension id of the container (could be base)
   * @param  {String} path
   * @param  {Container} container
   * @return {String}
   */
  const getBasePathForLayout = (path, container) => {
    const pathElements = Utils.addTrailingSlash(path).split('/');

    // If the extensionId is base, we need to insert base into the path,
    // e.g., dynamicLayouts/layoutId => dynamicLayouts/base/layoutId.
    if (container.extensionId === Constants.ExtensionNamespaces.BASE) {
      pathElements.splice(1, 0, Constants.ExtensionNamespaces.BASE);
    } else if (pathElements[1] !== Constants.ExtensionNamespaces.BASE) {
      // substitute the extension id, e.g., dynamicLayouts/self/foo -> dynamicLayouts/extA/foo,
      // except for dynamicLayouts/base/foo
      pathElements[1] = container.extensionId;
    }

    return pathElements.join('/');
  };

  /**
   * A class to retrieve the extensions for the current application from the extension manager
   * The extension manager URL is defined in the app-flow.json under the extension property.
   */
  class ExtensionRegistry {
    /**
     * This is called from ConfigLoader.js
     */
    constructor(registryConfig) {
      /** @type {Promise<Array<Extension>>|null} */
      this._extensionsPromise = null; // Initialized in getExtensions()
      this._appUiInfos = new AppUiInfos(); // Value is populated by updateAppUiInfos()

      /**
       * A map of extension object keyed by their id
       * The only purpose is to provide a fast response to getExtensionById()
       * @type {Object.<string, Extension>}
       */
      this._extensions = Object.create(null);

      /** @type {Object.<string, Array<Extension>>} */
      this._extensionsByDependencyId = Object.create(null);

      /** @type {Object.<string, Array<Extension>>} */
      this._extensionsByFilePath = Object.create(null);
      this._indexedExtensionCount = 0;

      /**
       * Monotonically increasing version of the live extension metadata. Consumers that perform asynchronous work
       * against the registry can discard and restart results when a manifest application changes this value.
       * @type {number}
       */
      this._manifestGeneration = 0;

      this._digestState = registryConfig && registryConfig.digestState;
      /**
       * Promise for the manifest digest promises used by _loadManifest().
       * Subclasses set this to the digest source for their loading mode:
       * full digest registries provide the full manifest array, while partial digest registries provide
       * the initial partial manifest promise.
       * @type {Promise<Array<Promise>>}
       * @protected
       */
      this._fetchManifestPromise = null;

      /**
       * RequireJS metadata accumulated by extension id from each loaded digest.
       * Full digest mode populates it once, while partial digest mode adds entries as App UI digests
       * are loaded on demand so Extension objects keep their bundle and component metadata.
       * @type {Object.<string, Object>}
       * @private
       */
      this._requirejsInfoByExtensionId = Object.create(null);

      /**
       * Highest-version external component metadata seen by this registry. Full digest processing rebuilds
       * this map from the complete manifest, while partial digest processing accumulates it as App UIs load.
       * @type {Object.<string, Object>}
       * @private
       */
      this._externalComponents = Object.create(null);

      /**
       * A promise for the full app UI info, used to cache the result.
       * Initialized in _getFullAppUiInfoMap()
       * @type {Promise<Object>?}
       */
      this._fullAppUiInfoPromise = null;

      /**
       * True when the application has an extension registry
       * @type {Boolean}
       */
      this._hasRegistry = registryConfig && registryConfig.hasRegistry();

      this.log = logger;

      if (this._hasRegistry) {
        // app-ui-info is the 3rd element in the manifest array of promises. Keep the original
        // full digest promise as well: _appUiInfoDigestPromise is released after the first
        // normalization, but a DT manifest refresh still needs the complete App UI catalog.
        this._fullAppUiInfoDigestPromise = Array.isArray(registryConfig.fetchFullManifestArray)
          ? registryConfig.fetchFullManifestArray[2]
          : Promise.resolve(registryConfig.fetchFullManifestArray).then((manifest) => manifest[2]);
        this._appUiInfoDigestPromise = this._fullAppUiInfoDigestPromise;
      } else {
        this.log.info('No extension registry defined.');

        // There is no extension manager defined
        // for this application so the manifest is an empty array.
        this._extensionsPromise = Promise.resolve([]);
      }
    }

    /**
     * True when the application has a registry (webapp versus appUi)
     * @type {Boolean}
     */
    get hasRegistry() {
      return this._hasRegistry;
    }

    /**
     * Version of the extension metadata currently exposed by this registry.
     * @return {number}
     */
    get manifestGeneration() {
      return this._manifestGeneration;
    }

    /**
     * Wait until manifest metadata is stable for asynchronous consumers. Full registries do not apply manifests in
     * the background, while partial registries override this method to wait for queued manifest applications.
     * @return {Promise<number>} stable manifest generation
     */
    whenManifestStable() {
      return Promise.resolve(this._manifestGeneration);
    }

    /**
     * Resolve a value against stable registry metadata. If a manifest application overlaps the resolver, invalidate
     * any consumer state derived from the previous generation and resolve the value again.
     *
     * @template T
     * @param {function(): (T|Promise<T>)} resolver operation that produces the value
     * @param {function(): void} [onManifestChange] invalidates consumer state derived from the previous generation
     * @return {Promise<T>} value produced against a stable manifest generation
     */
    resolveWithStableManifest(resolver, onManifestChange = () => {}) {
      const resolve = () => this.whenManifestStable()
        .then((generation) => Promise.resolve()
          .then(resolver)
          .then(
            (value) => ({ status: 'fulfilled', value }),
            (reason) => ({ status: 'rejected', reason }),
          )
          .then((result) => this.whenManifestStable()
            .then((stableGeneration) => {
              if (stableGeneration !== generation) {
                onManifestChange();
                return resolve();
              }
              const settledResult = /** @type {*} */ (result);
              if (settledResult.status === 'rejected') {
                throw settledResult.reason;
              }
              return settledResult.value;
            })));
      return resolve();
    }

    /**
     * Force the reload of the manifest and refresh existing Extension objects.
     * This is only called by DT after the DT manifest has been changed.
     * See RuntimeManager.refreshExtensionManifest()
     */
    refreshFromDT() {
      // Call _getExtensionPromise with refreshExisting=true so that existing extension objects are refreshed
      // instead of re-created.
      this._fullAppUiInfoPromise = null;
      this._extensionsPromise = this._getExtensionPromise(true)
        .then(async (extensions) => {
          // Keep the AppUiInfos object because Application retains a reference to it.
          this._appUiInfos.clear();
          await this.updateAppUiInfos(extensions);
          return extensions;
        });

      return this._extensionsPromise;
    }

    /**
     * Load extension manager and design time manifest and merge them
     * Only called by subclass
     * @return {Promise<Array<Promise>>}
     */
    async _loadManifest() {
      // Load the manifest from the extension manager and from DT and replace
      // the extensions and requirejsInfo with the ones from the DT manifest.
      const dtManifest = await (await Utils.getRuntimeEnvironment()).getExtensionManifest();
      const fullAppUiInfoDigestPromise = this._getFullAppUiInfoDigestPromise(dtManifest && dtManifest.appUiInfo);
      if (dtManifest) {
        let [
          runtimeDigestPromise,
          requirejsInfoDigestPromise,
          appUiInfoDigestPromise,
        ] = await this._fetchManifestPromise;
        // Replace or append manifest extensions using DT manifest
        if (runtimeDigestPromise) {
          runtimeDigestPromise = runtimeDigestPromise
            .then((extensions) => {
              Utils.replaceOrAppendToArray(dtManifest.extensions, extensions);
              return extensions;
            });
        }

        // Replace or append manifest requirejsInfo using DT extensions requirejsInfo
        if (requirejsInfoDigestPromise) {
          requirejsInfoDigestPromise = requirejsInfoDigestPromise
            .then((requirejsInfo) => {
              Utils.replaceOrAppendToArray(dtManifest.requirejsInfo, requirejsInfo);
              return requirejsInfo;
            });
        }

        // Replace or append manifest appUiInfo using DT extensions appUiInfo
        if (appUiInfoDigestPromise) {
          appUiInfoDigestPromise = appUiInfoDigestPromise
            .then((appUiInfo) => {
              const mergedAppUiInfo = Array.isArray(appUiInfo) ? appUiInfo.slice() : [];
              Utils.replaceOrAppendToArray(dtManifest.appUiInfo, mergedAppUiInfo);
              return mergedAppUiInfo;
            });
        }

        return [
          runtimeDigestPromise,
          requirejsInfoDigestPromise,
          appUiInfoDigestPromise,
          fullAppUiInfoDigestPromise,
        ];
      }

      const [
        runtimeDigestPromise,
        requirejsInfoDigestPromise,
        appUiInfoDigestPromise,
      ] = await this._fetchManifestPromise;
      return [runtimeDigestPromise, requirejsInfoDigestPromise, appUiInfoDigestPromise, fullAppUiInfoDigestPromise];
    }

    /**
     * Constructor for an extension
     * @param  {Object} def             definition from the manifest
     * @param  {Object} appUiInfo       the appUiInfo from the manifest for this extension
     * @param  {Array} bundleIds        array of bundle ids
     * @param  {Array} bundledResources array of bundled resources
     * @param  {Object} componentsRequirejs the requirejs config for the components defined in the extension
     * @return {Extension} the new extension
     */
    createExtension(def, appUiInfo, bundleIds, bundledResources, componentsRequirejs) {
      return new Extension(def, appUiInfo, bundleIds, bundledResources, componentsRequirejs, this);
    }

    /**
     * Rebuild registry indexes used for extension dependency and file lookups.
     * @param {Array<Extension>} extensions
     * @private
     */
    _rebuildExtensionIndexes(extensions) {
      /** @type {Object.<string, Array<Extension>>} */
      const dependentExtensionsById = Object.create(null);
      /** @type {Object.<string, Array<Extension>>} */
      const extensionsByFilePath = Object.create(null);

      extensions.forEach((extension) => {
        // eslint-disable-next-line no-underscore-dangle
        extension._invalidateDependencyCaches();
        Object.keys(extension.dependencies || {}).forEach((dependencyId) => {
          dependentExtensionsById[dependencyId] = dependentExtensionsById[dependencyId] || [];
          dependentExtensionsById[dependencyId].push(extension);
        });
        extension.files.forEach((filePath) => {
          extensionsByFilePath[filePath] = extensionsByFilePath[filePath] || [];
          extensionsByFilePath[filePath].push(extension);
        });
      });

      this._extensionsByDependencyId = dependentExtensionsById;
      this._extensionsByFilePath = extensionsByFilePath;
      this._indexedExtensionCount = extensions.length;
    }

    /**
     * Return matching extensions from the registry file index when it reflects the current extension list,
     * otherwise fall back to scanning the resolved extensions array directly.
     * @param {Array<Extension>} extensions
     * @param {String} filePath
     * @returns {Array<Extension>}
     * @private
     */
    _getMatchingExtensions(extensions, filePath) {
      if (extensions.length === this._indexedExtensionCount) {
        return this.getExtensionsWithFile(filePath);
      }
      return extensions.filter((extension) => extension.hasFile(filePath));
    }

    /**
     * Return all extensions declaring a dependency on the given extension id.
     * @param {String} extensionId
     * @returns {Array<Extension>}
     */
    getExtensionsDependingOn(extensionId) {
      return this._extensionsByDependencyId[extensionId] || [];
    }

    /**
     * Return all extensions containing the given file path.
     * @param {String} filePath
     * @returns {Array<Extension>}
     */
    getExtensionsWithFile(filePath) {
      return this._extensionsByFilePath[filePath] || [];
    }

    /**
     * Convert an app-ui-info digest array into a map keyed by extension id.
     * @param {Array<Object>} appUiInfo
     * @return {Object}
     * @protected
     */
    // eslint-disable-next-line class-methods-use-this
    _normalizeAppUiInfoDigest(appUiInfo) {
      const newAppUiInfo = {};

      if (appUiInfo) {
        appUiInfo.forEach((info) => {
          // Only create appUiInfo for extension with an App UI
          if (info.metadata && Array.isArray(info.metadata.applications) && info.metadata.applications.length > 0) {
            info.metadata.version = info.version;
            newAppUiInfo[info.id] = info.metadata;
          }
        });
      }

      return newAppUiInfo;
    }

    /**
     * Return a map of all App UI info from the full app-ui-info digest.
     * The digest promise can be provided when _loadManifest() has merged DT metadata into it.
     * Once normalized, only the compact map is kept so the raw digest array can be collected.
     * @param {Promise<Array<Object>>|Array<Object>} [appUiInfoDigest]
     * @return {Promise<Object>}
     * @protected
     */
    _getFullAppUiInfoMap(appUiInfoDigest = this._appUiInfoDigestPromise) {
      if (!this._fullAppUiInfoPromise) {
        this._fullAppUiInfoPromise = Promise.resolve(appUiInfoDigest)
          .then((appUiInfo) => this._normalizeAppUiInfoDigest(appUiInfo))
          .then((normalizedAppUiInfo) => {
            this._appUiInfoDigestPromise = null;
            return normalizedAppUiInfo;
          });
      }

      return this._fullAppUiInfoPromise;
    }

    /**
     * Return the complete App UI digest, with the current DT overrides applied.
     * A copy is used because a manifest refresh must not modify the digest cached by the adapter.
     * @param {Array<Object>} [dtAppUiInfo] App UI metadata provided by DT
     * @return {Promise<Array<Object>|undefined>}
     * @protected
     */
    _getFullAppUiInfoDigestPromise(dtAppUiInfo) {
      return Promise.resolve(this._fullAppUiInfoDigestPromise)
        .then((appUiInfo) => {
          if (!Array.isArray(dtAppUiInfo)) {
            return appUiInfo;
          }

          const mergedAppUiInfo = Array.isArray(appUiInfo) ? appUiInfo.slice() : [];
          Utils.replaceOrAppendToArray(dtAppUiInfo, mergedAppUiInfo);
          return mergedAppUiInfo;
        });
    }

    /**
     * Recreate the app-ui-info digest shape from a normalized app-ui-info map.
     * @param {Object} appUiInfoByExtensionId
     * @return {Array<Object>}
     * @private
     */
    // eslint-disable-next-line class-methods-use-this
    _getAppUiInfoDigestFromMap(appUiInfoByExtensionId) {
      return Object.entries(appUiInfoByExtensionId)
        .map(([id, metadata]) => ({
          id,
          version: metadata.version,
          metadata,
        }));
    }

    /**
     * Create a requirejs config with the bundle info from the digest.
     * @param {Array<Object>} requirejsInfo
     * @return {Object}
     * @protected
     */
    _processRequirejsInfoDigest(requirejsInfo = []) {
      const bundles = {};
      const bundlesInfo = {};
      const bundledResources = {};
      // Master list of external components (absolute path). A full manifest is authoritative, while partial
      // manifests must retain the highest version seen as each App UI digest is loaded.
      const externalComponents = this._digestState === Constants.DigestState.FULL
        ? Object.create(null)
        : this._externalComponents;
      // Per-extension list of components
      const components = {};

      requirejsInfo.forEach((info) => {
        bundlesInfo[info.id] = [];
        const { metadata } = info;

        if (metadata) {
          if (metadata.configurations) {
            // Check bundles defined in the extension
            const { build } = metadata.configurations;
            if (build) {
              if (build.bundles && typeof build.bundles === 'object') {
                const bundleIds = bundlesInfo[info.id];
                Object.keys(build.bundles).forEach((bundleId) => {
                  bundleIds.push(bundleId);
                });
                Object.assign(bundles, build.bundles);
              }

              if (build.bundledResources) {
                bundledResources[info.id] = build.bundledResources;
              }
            }
          }

          if (metadata.components) {
            // entries in the components section might used expression like window.vbInitConfig or $initParams
            const componentsDef = ConfigLoader.getEvaluatedSafe(metadata.components);
            Object.entries(componentsDef || {}).forEach(([name, componentInfo]) => {
              const paths = componentInfo.requirejs && componentInfo.requirejs.paths;
              if (Utils.isObject(paths) && Object.keys(paths).length > 0) {
                let path = paths[name];
                // For reference component, the path key if not the component name.
                // For example the name can be "oj-ref-moment" but for the path, the name is "moment"
                if (!path) {
                  // the logic is to take the first entry, supposedly the only one
                  [name] = Object.keys(paths);
                  path = paths[name];
                }

                if (Utils.isAbsoluteUrl(path)) {
                  // NOTE: If the logic for "keep the newest version of the component" changes, please make
                  // sure src/sw/extensionCacheController's component version checking is synched with it.
                  if (!componentInfo.version) {
                    this.log.warn('Component', name, 'does not have a version property, ignoring it.');
                  } else if (!externalComponents[name]
                      // Only keep the newest version of the component
                      || compareVersions(componentInfo.version, externalComponents[name].version) > 0) {
                    externalComponents[name] = componentInfo;
                  }
                } else {
                  components[info.id] = Utils.mergeObject(components[info.id] || {}, componentInfo.requirejs);
                }
              }
            });
          }
        }
      });

      // Configure the requirejs path and bundle info of all external components immediately
      // For each component only the latest version is used
      // Component local to extensions will be configured when the extension is initialized
      const paths = {};
      Object.entries(externalComponents).forEach(([name, comp]) => {
        this.log.info('Registering component', name, 'version', comp.version);
        Object.assign(paths, comp.requirejs.paths);
        Object.assign(bundles, comp.requirejs.bundles);
      });

      ConfigLoader.setConfiguration({ paths, bundles });

      this._externalComponents = externalComponents;

      // Remember the external components, so we can make sure extension component references have
      // the correct version for caching.
      const isCachingEnabled = Extension.isCachingEnabled();
      if (isCachingEnabled) {
        this.externalComponents = externalComponents;
      }

      // The bundlesInfo will be used during the extension creation the map the bundle URL
      // Local components will be initialized later when the extension is loaded
      return { bundlesInfo, bundledResources, components };
    }

    /**
     * Remember RequireJS metadata from the current manifest. Partial digest metadata accumulates as
     * additional App UIs are loaded, while a refreshed full manifest replaces the prior metadata.
     * @param {Object} requirejsInfo
     * @param {Boolean} [replaceExisting=false]
     * @private
     */
    _rememberRequirejsInfo(requirejsInfo = {}, replaceExisting = false) {
      if (replaceExisting) {
        this._requirejsInfoByExtensionId = Object.create(null);
      }

      const { bundlesInfo = {}, bundledResources = {}, components = {} } = requirejsInfo;

      Object.keys(bundlesInfo).forEach((extensionId) => {
        const current = this._requirejsInfoByExtensionId[extensionId] || {
          bundleIds: [],
          bundledResources: [],
          components: {},
        };

        current.bundleIds = Array.from(new Set(current.bundleIds.concat(bundlesInfo[extensionId] || [])));
        current.bundledResources = Array.from(new Set(current.bundledResources
          .concat(bundledResources[extensionId] || [])));
        current.components = Utils.mergeObject(current.components, components[extensionId] || {});

        this._requirejsInfoByExtensionId[extensionId] = current;
      });
    }

    /**
     * Returns true when an existing extension object should be refreshed from the manifest definition.
     * @param {Boolean} refreshExisting
     * @return {Boolean}
     * @protected
     */
    // eslint-disable-next-line class-methods-use-this
    _shouldRefreshExistingExtension(refreshExisting) {
      return refreshExisting;
    }

    /**
     * Return the definition to use when refreshing an existing extension.
     * @param {Extension} currentExtension
     * @param {Object} definition
     * @return {Object}
     * @protected
     */
    // eslint-disable-next-line class-methods-use-this
    _getRefreshExtensionDefinition(currentExtension, definition) {
      return definition;
    }

    /**
     * Return the extension array represented by the current manifest application.
     * @param {Array<Extension>} manifestExtensions
     * @return {Array<Extension>}
     * @protected
     */
    // eslint-disable-next-line class-methods-use-this
    _getAppliedManifestExtensions(manifestExtensions) {
      return manifestExtensions;
    }

    /**
     * Create or update Extension objects from normalized digest metadata.
     * @param {Array<Object>} extArray
     * @param {Object} requirejsInfo
     * @param {Object} appUiInfo
     * @param {Boolean} [refreshExisting=false]
     * @return {Promise<Array<Extension>>}
     * @protected
     */
    async _applyManifest(extArray, requirejsInfo, appUiInfo, refreshExisting = false) {
      this._rememberRequirejsInfo(requirejsInfo,
        refreshExisting && this._digestState === Constants.DigestState.FULL);

      // From this point to when endDeferRequireConfig is called, all calls
      // to ConfigLoader.setConfiguration are cached.
      ConfigLoader.startDeferRequireConfig();

      try {
        const extensionsToInit = [];
        const manifestExtensions = [];

        extArray.forEach((definition) => {
          const extensionId = definition.id;
          const currentExtension = this._extensions[extensionId];
          const rjsInfo = this._requirejsInfoByExtensionId[extensionId] || {};

          if (!currentExtension) {
            const extension = this.createExtension(definition,
              appUiInfo && appUiInfo[extensionId],
              rjsInfo.bundleIds,
              rjsInfo.bundledResources,
              rjsInfo.components);

            if (!extension.isValid()) {
              this.log.error('Invalid manifest for extension:', extensionId, 'version:', extension.version);
            } else {
              extensionsToInit.push(extension);
              this._extensions[extensionId] = extension;
              manifestExtensions.push(extension);
            }
          } else if (this._shouldRefreshExistingExtension(refreshExisting)) {
            currentExtension._applyDefinition(this._getRefreshExtensionDefinition(currentExtension, definition),
              appUiInfo && appUiInfo[extensionId],
              rjsInfo.bundleIds,
              rjsInfo.bundledResources,
              rjsInfo.components);
            if (!currentExtension.extendsHostApp) {
              extensionsToInit.push(currentExtension);
            }
            manifestExtensions.push(currentExtension);
          } else {
            manifestExtensions.push(currentExtension);
          }
        });

        const fullArray = this._getAppliedManifestExtensions(manifestExtensions);
        this._rebuildExtensionIndexes(fullArray);
        this._manifestGeneration += 1;

        // Initialize any extension of the host app as well as dependent extensions.
        // This will load the bundle or create a require mapping so the resources are available.
        // Extensions with only App UI definition or library of resources are loaded on demand later.
        const hostAppExtensions = extensionsToInit
          .filter((extension) => extension.extendsBaseArtifact());

        const promises = [];
        hostAppExtensions.forEach((ext) => {
          this.log.info('Extension', ext.id, 'extends the host app');
          ext.extendsHostApp = true;
          promises.push(ext.init());
        });

        await Promise.all(promises);

        return fullArray;
      } finally {
        ConfigLoader.endDeferRequireConfig();
      }
    }

    /**
     * Get the base path for the given container. If the container is a layout,
     * getBasePathForLayout will be called, otherwise, getBasePathForUi is called.
     *
     * @param {string} path
     * @param {Container} container
     * @returns {string}
     */
    // eslint-disable-next-line class-methods-use-this
    getBasePathForContainer(path, container) {
      return path.startsWith(Constants.DefaultPaths.LAYOUTS)
        ? getBasePathForLayout(path, container)
        : getBasePathForUi(path, container);
    }

    /**
     * Return the manifest digest promises to use when applying the initial extension registry metadata.
     * Subclasses implement this hook to normalize the digest metadata for their loading mode before
     * _getExtensionPromise() resolves the runtime, requirejs-info, and app-ui-info digests.
     * @return {Promise<Array<Promise>>}
     * @protected
     */
    _getLoadManifestPromise() {
      throw new Error('_getLoadManifestPromise must be implemented by an extension registry subclass.');
    }

    /**
     * Retrieve the promise to an array of Extension object
     * Loads the manifest metadata and create an extension object for each extension in the digest.
     * This is called in
     * @param {Boolean} [refreshExisting=false]
     * @return {Promise<Array<Extension>>}
     */
    async _getExtensionPromise(refreshExisting = false) {
      try {
        const manifestPromises = await this._getLoadManifestPromise();
        const [extArray, requirejsInfo, appUiInfo] = await Promise.all(manifestPromises);

        this.log.info('Found', extArray.length, 'extension(s) in the', this._digestState, 'manifest.');

        const fullArray = await this._applyManifest(extArray, requirejsInfo, appUiInfo, refreshExisting);
        this._afterInitialManifestApplied();
        return fullArray;
      } catch (err) {
        this._extensionsByDependencyId = Object.create(null);
        this._extensionsByFilePath = Object.create(null);
        this._indexedExtensionCount = 0;
        // Swallow the error so that it doesn't break the application, but no extension will be loaded
        this.log.error('Error loading extension registry, no App UI will be loaded', err);
        return [];
      }
    }

    /**
     * Hook called after the first manifest has been applied.
     * @protected
     */
    // eslint-disable-next-line class-methods-use-this
    _afterInitialManifestApplied() {
    }

    /**
     * Returns a promise that resolves with an array of extensions or an empty array
     * @return {Promise<Array<Extension>>} a promise that resolve to an array of extension
     */
    getExtensions() {
      this._extensionsPromise = this._extensionsPromise || this._getExtensionPromise();
      return this._extensionsPromise;
    }

    /**
     * Ensure the digest for an App UI has been loaded.
     * Full digest registries already contain all App UI metadata.
     * @param {String} appUiId canonical App UI id used by navigation
     * @return {Promise<boolean>} true when a new digest was loaded
     */
    // eslint-disable-next-line class-methods-use-this
    async loadAppUiDigest(appUiId) {
      return false;
    }

    /**
     * Populate AppUiInfos from a normalized app-ui-info map.
     * @param {Object} appUiInfoByExtensionId
     * @protected
     */
    _addAppUiInfosFromDigest(appUiInfoByExtensionId = {}) {
      Object.entries(appUiInfoByExtensionId).forEach(([extensionId, metadata]) => {
        const extension = this._extensions[extensionId];
        const infos = (metadata && metadata.applications) || [];
        infos.forEach((info) => {
          this._appUiInfos.add(info.id, extension, info);
        });
      });
    }

    /**
     * Retrieve a map of AppUiInfo for all the App UI available in all the extensions
     * @return {Promise} a promise that resolve when the appUiInfo are updated
     */
    async updateAppUiInfos(extensions) {
      try {
        const runtimeEnv = await Utils.getRuntimeEnvironment();
        // For previewing root page, DT needs to disable App UIs
        const disableAppUis = await runtimeEnv.disableAppUis();
        // App UIs are disabled by setting _appUiInfos to an empty appUiInfos so that
        // when getAppUiInfos() is called, an AppUIInfos object without any App UI is returned.
        if (disableAppUis === true) {
          this._appUiInfos.clear();
          return;
        }

        const extensionsToProcess = extensions || await this.getExtensions();

        // Traverse the array of extension from first to last. The extension manager is responsible
        // for properly ordering this array of extensions given the dependencies in the extension manager.
        extensionsToProcess.forEach((extension) => {
          const infos = (extension.appUiInfo && Object.values(extension.appUiInfo)) || [];
          if (infos.length > 0) {
            infos.forEach((info) => {
              if (!this._appUiInfos.exist(info.id)) {
                this._appUiInfos.add(info.id, extension, info);
              }
            });
          }
        });
      } catch (err) {
        // Initialize with an empty AppUiInfos so that the application can still run, but no App UI will be loaded
        this._appUiInfos.clear();

        // Swallow the error so that it doesn't break the application, but no App UI will be loaded
        this.log.error('Error updating App UI Infos, no App UI will be available', err);
      }
    }

    /**
     * A promise that resolve when the registry is initialized.
     * It only consist of updating the AppUiInfos object.
     *
     * @return {Promise}
     */
    initRegistry() {
      return Promise.resolve(this.updateAppUiInfos()).then(() => undefined);
    }

    /**
     * Return an AppUiInfos object
     *
     * @return {AppUiInfos}
     */
    getAppUiInfos() {
      return this._appUiInfos;
    }

    /**
     * Return a promise to the appUiInfo digest metadata
     * @return {Promise<Array<Object>|undefined>}
     */
    getAppUiInfoDigest() {
      if (this._appUiInfoDigestPromise) {
        return Promise.resolve(this._appUiInfoDigestPromise);
      }

      return this._getFullAppUiInfoMap()
        .then((appUiInfoByExtensionId) => this._getAppUiInfoDigestFromMap(appUiInfoByExtensionId));
    }

    /**
     * Loads all the extension for a specific container given its path. It returns a promise
     * that resolves in an array of extensions object, either PageExtension or FlowExtension.
     * @param  {String} path the path of the object for which we are looking for extensions
     * @param  {Container} container the container for which the extension is being loaded
     * @return {Promise} a promise to an array of extension objects
     */
    loadContainerExtensions(path, container) {
      return this.getExtensions()
        .then((extensions) => {
          const basePath = this.getBasePathForContainer(path, container);
          /** @type {*} */
          const containerClass = container.constructor;
          const ExtensionClass = containerClass.extensionClass;
          // container name may not necessarily be the actual resourceName. So use the resourceName property instead
          const extPath = `${basePath}${container.extensionResourceName}${ExtensionClass.resourceSuffix}`;
          const matchingExtensions = this._getMatchingExtensions(extensions, extPath);
          if (matchingExtensions.length === 0) {
            return [];
          }

          const promises = [];
          const isOnline = Utils.isOnline();

          // Traverse the array of extension from first to last. The extension manager is responsible
          // for properly ordering this array of extensions given the dependencies in the extension manager.
          matchingExtensions.forEach((extension) => {
            // If the manifest contains an extension for this artifact, creates an extension object for it
            // But only if we're online or the extension is cacheable.
            // (This assumes that if the extension is cacheable, it has been cached.)
            if (isOnline || extension.checkCacheable()) {
              // TODO: reduce params?
              const ext = new ExtensionClass(extension, basePath, container);
              const promise = ext.load().then(() => ext);
              promises.push(promise);
            }
          });

          // All files are then loaded in parallel
          return Promise.all(promises);
        });
    }

    /**
     * Loads all the extensions for a specific V2 Bundle given its normalized path. (self has already been mapped,
     * as well as any other translations-specific path manipulation)
     * It returns a promise that resolves in an array of V2 Bundle Extension objects.
     * @param  {String} path the path of the V2 Bundle Definition for which we are looking for extensions
     * @param  {BundleV2Definition} bundleDefinition the bundle for which the extensions are being loaded
     * @return {Promise} a promise to an array of V2 Bundle Extension objects
     */
    loadTranslationExtensions(path, bundleDefinition) {
      return this.getExtensions().then((extensions) => {
        const extensionPath = `${path}-x`;
        /** @type {*} */
        const bundleClass = bundleDefinition.constructor;
        const ExtensionClass = bundleClass.extensionClass;
        const extPath = `${extensionPath}.js`;
        const matchingExtensions = this._getMatchingExtensions(extensions, extPath);
        const promises = [];

        // Traverse the array of extension from first to last. The extension manager is responsible
        // for properly ordering this array of extensions given the dependencies in the extension manager.
        matchingExtensions.forEach((extension) => {
          const ext = new ExtensionClass(extension, extensionPath, bundleDefinition);
          const promise = ext.load().then(() => ext);
          promises.push(promise);
        });

        // All files are then loaded in parallel
        return Promise.all(promises);
      });
    }

    /**
     * Retrieve a map of all extensions that define translation bundles.
     * @return {Promise<Object<string, Object>>} map of extId to extension for all that define a translation bundle
     */
    getTranslations() {
      const translationsConfigPath = `${Constants.DefaultPaths.TRANSLATIONS}translations-config.json`;
      return this.getExtensions().then((extensions) => {
        const results = {};

        this._getMatchingExtensions(extensions, translationsConfigPath).forEach((extension) => {
          results[extension.id] = extension;
        });

        return results;
      });
    }

    /**
     * Look up the extension identified by id.
     *
     * @param {String} id extension id
     * @returns {Extension}
     */
    getExtensionById(id) {
      return this._extensions[id];
    }

    /**
     * This function checks if an extension depends directly or indirectly on another extension given their ids.
     *
     * @param {string} extensionId
     * @param {string} upstreamExtensionId
     * @returns {boolean} true if extensionId depends on upstreamExtensionId
     */
    isDependent(extensionId, upstreamExtensionId) {
      // extensions do not need to declare dependency on 'base'
      if (upstreamExtensionId === Constants.ExtensionNamespaces.BASE) {
        return true;
      }

      const checked = {};
      const processing = {};
      const toCheck = [];
      let current = extensionId;
      while (current) {
        if (current === upstreamExtensionId) {
          return true;
        }
        checked[current] = true;

        const extension = this.getExtensionById(current);
        if (extension && extension.dependencies) {
          Object.keys(extension.dependencies).forEach((depId) => {
            if (!checked[depId] && !processing[depId]) {
              toCheck.push(depId);
              processing[depId] = true;
            }
          });
        }
        current = toCheck.shift();
      }
      return false;
    }

    /**
     * Add requirejs mappings
     * @param {Object.<String, String>} paths
     */
    // eslint-disable-next-line class-methods-use-this
    addRequireMapping(paths) {
      ConfigLoader.setConfiguration({ paths });
    }
  }

  return ExtensionRegistry;
});

